import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box, Button, Typography, CircularProgress,
  IconButton, Paper, Snackbar, Alert as MuiAlert,
} from "@mui/material";
import { ArrowBack, CheckCircle, SaveOutlined } from "@mui/icons-material";
import { gqlRequest, UPDATE_BUSINESS_USAGE } from "../../api/graphql";
import { useAuth } from "../../store/AuthContext";
import { fmtDate } from "../../utils/helpers";
import { COLORS } from "../../theme";
import { PageWrapper, BizAvatar, StatusChip } from "../../components/UI";
import UsageForm from "../../components/UsageForm";

// Plan/identifier fields kept in the form but rendered as disabled/readonly —
// shown for context but not editable here.
const DISABLED_FIELDS = ["locationid", "trialplanid", "paymentid", "subscriptionid", "curruntplanid"];

// startdate/enddate come back from the API as numeric-string epoch ms (e.g. "1635483730000").
// `.slice(0, 10)` on that string produces garbage like "1635483730", not a valid
// "YYYY-MM-DD" — which is why the date pickers were showing invalid/blank. Convert
// the epoch value to a real Date first, then format it the way <input type="date"> expects.
const epochToDateInput = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const ms = typeof val === "number" ? val : parseInt(val, 10);
  if (Number.isNaN(ms)) return "";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

// Reverse conversion for display (Current Record summary) — same numeric-string-epoch fix.
const fmtTimestamp = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const ms = typeof val === "number" ? val : parseInt(val, 10);
  if (Number.isNaN(ms)) return null;
  return fmtDate(ms);
};

export default function EditUsagePage() {
  const { businessid, usageid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const biz = location.state?.biz || { entityid: businessid };
  const rawUsage = location.state?.usage;

  // Pre-fill form from existing usage data — same field set CREATE_BUSINESS_USAGE accepts,
  // so the same payload shape can be sent back on update.
  const [form, setForm] = useState(() => {
    if (!rawUsage) return { businessid, isactive: true };
    return {
      startdate: epochToDateInput(rawUsage.startdate),
      enddate: epochToDateInput(rawUsage.enddate),
      isactive: rawUsage.isactive ?? true,
      // limits
      userslimit: rawUsage.userslimit ?? 0,
      textsmslimit: rawUsage.textsmslimit ?? 0,
      emaillimit: rawUsage.emaillimit ?? 0,
      storagelimit: rawUsage.storagelimit ?? 0,
      calltimelimit: rawUsage.calltimelimit ?? 0,
      leadgatheringlimit: rawUsage.leadgatheringlimit ?? 0,
      socialpostlimit: rawUsage.socialpostlimit ?? 0,
      emailfinderlimit: rawUsage.emailfinderlimit ?? 0,
      leadfinderlimit: rawUsage.leadfinderlimit ?? 0,
      aiquerylimit: rawUsage.aiquerylimit ?? 0,
      linkedinuserlimit: rawUsage.linkedinuserlimit ?? 0,
      whatsappuserlimit: rawUsage.whatsappuserlimit ?? 0,
      instagramuserlimit: rawUsage.instagramuserlimit ?? 0,
      enrichsearchlimit: rawUsage.enrichsearchlimit ?? 0,
      enrichreveallimit: rawUsage.enrichreveallimit ?? 0,
      // unlimited
      isunlimitedusers: rawUsage.isunlimitedusers ?? false,
      isunlimitedtextsms: rawUsage.isunlimitedtextsms ?? false,
      isunlimitedemail: rawUsage.isunlimitedemail ?? false,
      isunlimitedstorage: rawUsage.isunlimitedstorage ?? false,
      isunlimitedcalltime: rawUsage.isunlimitedcalltime ?? false,
      isunlimitedleadgathering: rawUsage.isunlimitedleadgathering ?? false,
      isunlimitedsocialpost: rawUsage.isunlimitedsocialpost ?? false,
      isunlimitedemailfinder: rawUsage.isunlimitedemailfinder ?? false,
      isunlimitedleadfinder: rawUsage.isunlimitedleadfinder ?? false,
      isunlimitedaiquery: rawUsage.isunlimitedaiquery ?? false,
      // usage counters
      users: rawUsage.users ?? 0,
      textsms: rawUsage.textsms ?? 0,
      email: rawUsage.email ?? 0,
      storage: rawUsage.storage ?? 0,
      calltime: rawUsage.calltime ?? 0,
      leadgathering: rawUsage.leadgathering ?? 0,
      socialpost: rawUsage.socialpost ?? 0,
      emailfinder: rawUsage.emailfinder ?? 0,
      leadfinder: rawUsage.leadfinder ?? 0,
      aiquery: rawUsage.aiquery ?? 0,
      linkedinuser: rawUsage.linkedinuser ?? 0,
      whatsappuser: rawUsage.whatsappuser ?? 0,
      instagramuser: rawUsage.instagramuser ?? 0,  // ADD
      enrichsearch: rawUsage.enrichsearch ?? 0,
      enrichreveal: rawUsage.enrichreveal ?? 0,
    };
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const onChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const showToast = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast(p => ({ ...p, open: false }));

  const handleUpdate = async () => {
    if (form.startdate && form.enddate && form.enddate <= form.startdate) {
      showToast("error", "End date must be after start date.");
      return;
    }

    setLoading(true);
    try {
      const vars = {
        ...form,
        businessusageid: usageid,
        startdate: form.startdate,
        enddate: form.enddate ? form.enddate : undefined,
        isactive: form.isactive,
      };

      // Clean up: remove empty strings → undefined, same as create.
      Object.keys(vars).forEach(k => { if (vars[k] === "") vars[k] = undefined; });

      await gqlRequest(UPDATE_BUSINESS_USAGE, vars, token);
      setSuccess(true);
      showToast("success", "Usage updated successfully!");
      setTimeout(() => navigate(`/businesses/${businessid}/usage`, { state: { biz } }), 1500);
    } catch (e) {
      showToast("error", e.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton
          onClick={() => navigate(`/businesses/${businessid}/usage`, { state: { biz } })}
          sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 1.5 }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <BizAvatar biz={biz} size={38} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Edit Usage Record</Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            {biz.businessname || businessid} • ID: …{usageid?.slice(-10)}
          </Typography>
        </Box>
      </Box>

      {/* Current record summary */}
      {rawUsage && (
        <Paper sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${COLORS.border}`, mb: 3, boxShadow: "none", bgcolor: COLORS.surface }}>
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.muted, display: "block", mb: 1.5 }}>
            Current Record
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <StatusChip active={rawUsage.isactive} />
            <Typography variant="caption" sx={{ color: COLORS.textSub }}>
              {fmtTimestamp(rawUsage.startdate) || "—"} → {fmtTimestamp(rawUsage.enddate) || "No end"}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Editable form — Status field lives inside Plan & Dates; overlaid with a loader while saving */}
      <Box sx={{ position: "relative" }}>
        {loading && (
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.6)", borderRadius: 2,
          }}>
            <CircularProgress sx={{ color: COLORS.accent }} />
          </Box>
        )}

        <UsageForm
          form={form}
          onChange={onChange}
          isMultiple={false}
          showUsage={false}
          disabledFields={DISABLED_FIELDS}
        />
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/businesses/${businessid}/usage`, { state: { biz } })}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, borderColor: COLORS.border, color: COLORS.muted, px: 3 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={loading || success}
          sx={{
            bgcolor: COLORS.accent, "&:hover": { bgcolor: "#4338ca" },
            borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4, minWidth: 160,
          }}
        >
          {loading
            ? <CircularProgress size={20} color="inherit" />
            : success
              ? <><CheckCircle sx={{ mr: 1, fontSize: 18 }} />Updated!</>
              : <><SaveOutlined sx={{ mr: 1, fontSize: 18 }} />Save Changes</>
          }
        </Button>
      </Box>

      {/* Toast notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </PageWrapper>
  );
}